import axiosInstance from '../../api/axios';

// Row types mirror the backend row structs (and their `json` tags) verbatim —
// see skeptics-backend/repository/analytics_reports_repository.go. Do not
// rename fields without updating both sides of the contract.

/** Report #20 — Control Test Summary. */
export type ControllerTestSummaryRow = {
  control_id: string;
  title: string;
  workflow_status: string | null;
  overall_status: string | null;
  conclusion: string | null;
  attributes_passed: number | null;
  attributes_failed: number | null;
  total_exceptions: number | null;
  confidence_score: number | null;
  testing_period_start: string | null;
  testing_period_end: string | null;
};

/** Report #21 — Failing Controls Detail. */
export type ControllerFailingControlRow = {
  control_id: string;
  title: string;
  department: string | null;
  attributes_failed: number;
  total_exceptions: number;
  confidence_score: number | null;
};

/** Report #22 — Exception Rate by Control. */
export type ControllerExceptionRateRow = {
  control_id: string;
  title: string;
  pop: number | null;
  sample: number | null;
  avg_deviation: number | null;
  total_exceptions: number | null;
};

/** Report #23 — Sample Size Report. */
export type ControllerSampleSizeRow = {
  control_id: string;
  title: string;
  population: number | null;
  sample_size: number | null;
  sampling_method: string | null;
  deviation_rate: number | null;
};

/** Report #24 — Re-performance & Roll-forward Tracker. */
export type ControllerRollforwardRow = {
  control_id: string;
  title: string;
  run_number: number;
  is_rollforward: boolean;
  rerun_reason: string | null;
  workflow_status: string;
  created_at: string | null;
};

/** Report #25 — IPE Validation Status. */
export type ControllerIPEStatusRow = {
  control_id: string;
  title: string;
  ipe_validated: boolean | null;
  workflow_status: string | null;
};

/** Report #26 — ToD vs ToE Breakdown. */
export type ControllerToDToERow = {
  control_id: string;
  title: string;
  test_type: string | null;
  overall_status: string | null;
  conclusion: string | null;
  run_count: number;
};

/** Report #27 — Segregation of Duties Verified. */
export type ControllerSoDVerifiedRow = {
  control_id: string;
  title: string;
  segregation_verified: boolean | null;
  preparer_name: string | null;
  reviewer_name: string | null;
};

/** Report #28 — Evidence Coverage Map. */
export type ControllerEvidenceCoverageRow = {
  control_id: string;
  samples: number;
  covered: number;
  coverage_pct: number | null;
};

type RowsEnvelope<T> = { rows: T[] };

async function getRows<T>(path: string): Promise<T[]> {
  const response = await axiosInstance.get<RowsEnvelope<T>>(path);
  return response?.data?.rows ?? [];
}

const BASE = '/api/analytics/controller';

export const fetchControllerTestSummary = (): Promise<ControllerTestSummaryRow[]> =>
  getRows<ControllerTestSummaryRow>(`${BASE}/test-summary`);

export const fetchControllerFailingControls = (): Promise<ControllerFailingControlRow[]> =>
  getRows<ControllerFailingControlRow>(`${BASE}/failing-controls`);

export const fetchControllerExceptionRate = (): Promise<ControllerExceptionRateRow[]> =>
  getRows<ControllerExceptionRateRow>(`${BASE}/exception-rate`);

export const fetchControllerSampleSize = (): Promise<ControllerSampleSizeRow[]> =>
  getRows<ControllerSampleSizeRow>(`${BASE}/sample-size`);

export const fetchControllerRollforward = (): Promise<ControllerRollforwardRow[]> =>
  getRows<ControllerRollforwardRow>(`${BASE}/rollforward`);

export const fetchControllerIPEStatus = (): Promise<ControllerIPEStatusRow[]> =>
  getRows<ControllerIPEStatusRow>(`${BASE}/ipe-status`);

export const fetchControllerToDToE = (): Promise<ControllerToDToERow[]> =>
  getRows<ControllerToDToERow>(`${BASE}/tod-toe`);

export const fetchControllerSoDVerified = (): Promise<ControllerSoDVerifiedRow[]> =>
  getRows<ControllerSoDVerifiedRow>(`${BASE}/sod-verified`);

export const fetchControllerEvidenceCoverage = (): Promise<ControllerEvidenceCoverageRow[]> =>
  getRows<ControllerEvidenceCoverageRow>(`${BASE}/evidence-coverage`);
